import jwt_dec from 'jwt-decode';
import * as Sentry from 'raven-js';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ILogout, logout, setLoggedIn, setTokenChecked } from '../../actions/auth';
import api from '../../lib/api';
import Auth from '../../lib/auth';
import { Domain, Role, mapAccount } from '../../meta/types/account';
import { StoreState } from '../../meta/types/store';
import {
  Claims,
  DomainRoleAssignment,
  RoleAssignmentGroup,
  SentinelUser,
  toInternalUser,
  DomainAccountWithDomain,
} from '../../meta/types/user';
import UserResource from '../../services/Identity/UserResource';
import { ThunkDispatch } from '../../meta/types/actions';
import RoleResource from '../../services/Identity/RoleResource';
import { setUserSkin } from '../../lib/skin';
import { setAccounts } from '../../actions/accounts';
import AccountResource from '../../services/Identity/AccountResource';
import DomainResource from '../../services/Identity/DomainResource';
import { alertError, alertSuccess } from '../../lib/alert';

type PropsFromState = {
  tokenChecked: boolean;
};

type PropsFromDispatch = {
  setTokenChecked: (checked: boolean) => void;
  setLoggedIn: (user: SentinelUser, claims: Claims | undefined, rootManaging: boolean) => void;
  logout: () => Promise<ILogout>;
  setAccounts: (
    accounts: DomainAccountWithDomain[],
    shAccount: DomainAccountWithDomain | undefined,
    user: SentinelUser,
  ) => void;
};

export type AuthorizationProps = PropsFromState & PropsFromDispatch & RouteComponentProps;

const getAccountsWithDomains = async (userId: string): Promise<DomainAccountWithDomain[]> => {
  try {
    const resp = await AccountResource.list({ userId });
    const accounts = resp.member.map(mapAccount);
    return (await Promise.all(
      accounts.map(async acc => {
        const domain = await DomainResource.get({ domainId: acc.domainId });
        return { ...acc, domain };
      }),
    )) as DomainAccountWithDomain[];
  } catch (err) {
    return [];
  }
};

const withAuthorization = (WrappedComponent: React.ComponentClass<AuthorizationProps>) => {
  const Authorized = class extends React.Component<AuthorizationProps> {
    async componentDidMount() {
      const isAppManaged = await this.isRootUserManaging();
      if (isAppManaged) {
        return;
      }

      const authorization = Auth.getAuthorization();

      if (authorization !== undefined) {
        const now = new Date().valueOf();
        const expiration = authorization.expires_in;

        if (now < expiration) {
          const claims: Claims = jwt_dec(authorization.access_token);
          let prohibitUse = false;
          if (claims.d && claims.d[Domain.SH]) {
            const domainData: DomainRoleAssignment = claims.d[Domain.SH];
            const roleAssignmentGroup = await RoleResource.getRoleAssignmentGroup({
              groupId: domainData.ra.rag,
            });
            prohibitUse = this.prohibitUse(domainData, roleAssignmentGroup);
          }
          if (prohibitUse) {
            await this.props.logout();
          } else {
            const serverUser = await UserResource.get({ userId: claims.sub });
            const accountsWithDomains = await getAccountsWithDomains(claims.sub);
            const domainId = claims.did ?? 1;
            const acc = accountsWithDomains.find(acc => acc.domainId === domainId);
            const type = acc?.type;
            const user = toInternalUser(serverUser);
            const accountContext: { type?: any } = { type };
            Sentry.setUserContext({ ...accountContext, email: user.email, id: user.id });
            this.props.setAccounts(accountsWithDomains, acc, user);
            this.login(user, claims);
          }
        } else {
          Auth.deleteAuthorization();
        }
      }

      this.props.setTokenChecked(true);
    }

    prohibitUse(domainData: DomainRoleAssignment, roleAssignmentGroup: RoleAssignmentGroup) {
      return (
        roleAssignmentGroup !== undefined &&
        roleAssignmentGroup.assignments.dbd === Role.APPLICATION_RO
      );
    }

    async isRootUserManaging() {
      const search = window.location.hash.replace(/^[^?]+\?/, '');
      const queryParams = new URLSearchParams(search);
      const impersonatorToken = queryParams.get('impersonator_token');
      if (impersonatorToken) {
        const claims: Claims = jwt_dec(impersonatorToken);
        if (new Date() < new Date(claims.exp * 1000)) {
          api.setDefaultHeader('Authorization', `Bearer ${impersonatorToken}`);
          const serverUser = await UserResource.get({ userId: claims.sub });
          const accountsWithDomains = await getAccountsWithDomains(claims.sub);
          const acc = accountsWithDomains.find(acc => acc.domainId === claims.did);
          const user = toInternalUser(serverUser);
          this.props.setAccounts(accountsWithDomains, acc, user);
          alertSuccess(`Impersonating: ${claims.sub}`);
          return this.login(user, claims, true);
        }
        alertError('Impersonator token is expired.');
      }
      return false;
    }

    login(user: SentinelUser, claims: Claims, rootManaging = false) {
      this.props.setLoggedIn(user, claims, rootManaging);
      return true;
    }

    render() {
      const { tokenChecked } = this.props;
      return !tokenChecked ? null : <WrappedComponent {...this.props} />;
    }
  };

  const enhance = connect(mapStateToProps, mapDispatchToProps);

  return enhance(Authorized);
};

function mapStateToProps(state: StoreState): PropsFromState {
  return {
    tokenChecked: state.auth.tokenChecked,
  };
}

function mapDispatchToProps(dispatch: ThunkDispatch): PropsFromDispatch {
  return {
    setTokenChecked: tokenChecked => dispatch(setTokenChecked(tokenChecked)),
    setLoggedIn: (user, claims, rootManaging) => {
      dispatch(setLoggedIn(user, claims, rootManaging));
    },
    logout: () => dispatch(logout()),
    setAccounts: (
      accounts: DomainAccountWithDomain[],
      shAccount: DomainAccountWithDomain | undefined,
      user: SentinelUser,
    ) => {
      setUserSkin(user, shAccount);
      dispatch(setAccounts(accounts));
    },
  };
}

export default withAuthorization;
